import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Check, ArrowLeft, Sparkles, Crown, Gem, Plus } from 'lucide-react';
import { Footer, SubscriptionTerms } from '../components/LegalDisclaimers';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import { useIsDealScannerEnabled } from '../context/FeatureFlagsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Pricing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loadingCheckout, setLoadingCheckout] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [pricing, setPricing] = useState(null);

  const isYearly = billingPeriod === 'yearly';
  const dealScannerEnabled = useIsDealScannerEnabled();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) { /* Not logged in */ }
    };
    checkAuth();
    // Public pricing table with Quebec tax breakdown (TPS + TVQ).
    axios
      .get(`${BACKEND_URL}/api/subscription/pricing`)
      .then((r) => setPricing(r.data))
      .catch(() => setPricing(null));
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const handleCheckout = async (plan) => {
    if (!user) {
      handleLogin();
      return;
    }
    setLoadingCheckout(plan);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscription/checkout`, {
        origin_url: window.location.origin,
        billing_period: billingPeriod,
        plan: plan
      }, { withCredentials: true });
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
    } finally {
      setLoadingCheckout(null);
    }
  };

  const freeFeatures = [
    'pricing.features.analyses3',
    'pricing.features.dealScore',
    ...(dealScannerEnabled ? ['pricing.features.dealScannerFree'] : []),
    'pricing.features.export',
    'pricing.features.basicMetrics'
  ];

  const proFeatures = [
    'pricing.features.analysesUnlimited',
    ...(dealScannerEnabled ? ['pricing.features.dealScannerPro'] : []),
    'pricing.features.investorProfile',
    'pricing.features.myPortfolioHub',
    'pricing.features.scenarioAnalyzer',
    'pricing.features.comparison',
    'pricing.features.advancedMetrics',
    'pricing.features.export'
  ];

  const premiumFeatures = [
    'pricing.features.allProFeatures',
    ...(dealScannerEnabled ? ['pricing.features.dealScannerPremium'] : []),
    'pricing.features.portfolioDoctor',
    'pricing.features.actionCenter',
    'pricing.features.aiPropertyAdvisor',
    'pricing.features.portfolioHealth',
    'pricing.features.portfolioComparison',
    'pricing.features.portfolioGoals',
    'pricing.features.prioritySupport'
  ];

  const userPlan = user?.subscription_plan || 'free';
  const planRank = { free: 0, pro: 1, premium: 2 };

  const isCurrentPlan = (plan) => userPlan === plan;
  const isDowngrade = (plan) => planRank[plan] < planRank[userPlan];
  const currentBillingInterval = user?.billing_interval === 'year' ? 'yearly' : 'monthly';
  const isSameIntervalCurrent = (plan) => isCurrentPlan(plan) && currentBillingInterval === billingPeriod;

  const isPaidUser = userPlan === 'pro' || userPlan === 'premium';

  const refreshUser = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(r.data);
    } catch {
      /* ignore */
    }
  };

  const handleChangePlan = async (plan) => {
    setLoadingCheckout(plan);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/subscription/change-plan`,
        { plan, billing_period: billingPeriod },
        { withCredentials: true },
      );
      const d = resp.data || {};
      if (d.requires_action && d.hosted_invoice_url) {
        // Card authentication needed — send the user to Stripe's hosted invoice.
        window.location.href = d.hosted_invoice_url;
        return;
      }
      toast.success(t('pricing.planUpdated'));
      await refreshUser();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        if (detail.error === 'same_plan') {
          toast.info(t('pricing.samePlan'));
        } else if (detail.error === 'card_declined') {
          toast.error(t('pricing.cardDeclined'));
        } else if (detail.error === 'multi_item_subscription') {
          toast.error(t('pricing.multiItemSub'));
        } else if (detail.error === 'missing_stripe_ids') {
          toast.error(t('pricing.missingStripeIds'));
        } else if (detail.error === 'no_active_subscription') {
          // Fall through to new-subscription Checkout.
          await handleCheckout(plan);
          return;
        } else {
          toast.error(t('pricing.changeFailed'));
        }
      } else {
        toast.error(t('pricing.changeFailed'));
      }
    } finally {
      setLoadingCheckout(null);
    }
  };

  // Router: paid users invoking a different plan → modify existing sub.
  // Free users (or no active sub) → standard Checkout.
  const handlePlanClick = (plan) => {
    if (!user) {
      handleLogin();
      return;
    }
    if (isPaidUser) {
      handleChangePlan(plan);
    } else {
      handleCheckout(plan);
    }
  };

  const fmt = (n) =>
    typeof n === 'number' ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  const planPricing = (plan) => pricing?.[plan]?.[isYearly ? 'yearly' : 'monthly'] || null;

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 backdrop-saturate-150 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2">
              <RendPlexLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              {user ? (
                <>
                <Button onClick={() => navigate('/dashboard')} variant="ghost" className="text-slate-500 dark:text-slate-400">
                  <ArrowLeft className="w-4 h-4 mr-2" /> {t('nav.backToDashboard')}
                </Button>
                <Button data-testid="new-analysis-button" onClick={() => navigate('/analyze')} size="sm"
                  className="bg-[#12355B] hover:bg-[#1565C0] text-white dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#0F172A] font-semibold">
                  <Plus className="w-4 h-4 mr-1.5" /> {t('dashboard.newAnalysis')}
                </Button>
                </>
              ) : (
                <Button data-testid="pricing-login-button" onClick={handleLogin} className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl px-6 font-semibold transition-all duration-300">
                  {t('nav.signIn')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 lg:py-28 relative">
        {/* Background Property Image */}
        <div className="absolute inset-0 -mx-4 sm:-mx-6 lg:-mx-12 overflow-hidden">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/35251cdc-fe6f-4305-b40a-115bc9637197/images/6e16fd70b2939ca78f5767cfd44afba2c9adfee3f72c527185a2556ae3753ae9.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#F8FAFC]/[0.96] dark:bg-[#0B0E14]/[0.97] backdrop-blur-[2px]" />
        </div>

        <div className="relative text-center mb-10">
          <h1 className="font-primary font-extrabold text-4xl lg:text-5xl text-[#12355B] dark:text-white tracking-tighter mb-4">
            {t('pricing.title')}
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-lg mx-auto">
            {t('pricing.subtitle')}
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="relative flex items-center justify-center gap-3 mb-12" data-testid="billing-toggle">
          <span className={`text-sm font-medium transition-colors ${!isYearly ? 'text-[#12355B] dark:text-white' : 'text-slate-400'}`}>
            {t('pricing.monthly')}
          </span>
          <button
            data-testid="billing-toggle-switch"
            onClick={() => setBillingPeriod(isYearly ? 'monthly' : 'yearly')}
            className={`relative w-14 h-7 rounded-full transition-colors duration-300 focus:outline-none ${isYearly ? 'bg-[#45C04A]' : 'bg-slate-300 dark:bg-slate-600'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${isYearly ? 'translate-x-7' : 'translate-x-0'}`}
            />
          </button>
          <span className={`text-sm font-medium transition-colors ${isYearly ? 'text-[#12355B] dark:text-white' : 'text-slate-400'}`}>
            {t('pricing.yearly')}
          </span>
          {isYearly && (
            <span className="ml-1 bg-[#45C04A]/15 text-[#45C04A] text-xs font-bold px-2.5 py-1 rounded-full" data-testid="save-badge">
              {t('pricing.save20')}
            </span>
          )}
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {/* Free Plan */}
          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-card transition-all duration-300 hover:shadow-card-hover flex flex-col" data-testid="free-plan-card">
            <div className="mb-6">
              <h3 className="font-primary font-bold text-xl text-[#12355B] dark:text-white mb-2">{t('pricing.freePlan')}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('pricing.freePlanDescription')}</p>
            </div>
            <div className="mb-6">
              <span className="font-primary font-extrabold text-5xl text-[#12355B] dark:text-white tracking-tight">$0</span>
              <span className="text-slate-400 ml-2">/{t('pricing.proPlanCurrency')}</span>
            </div>
            <div className="space-y-3 mb-8 flex-1">
              {freeFeatures.map((key) => (
                <div key={key} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#45C04A] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">{t(key)}</span>
                </div>
              ))}
            </div>
            <Button
              data-testid="free-plan-button"
              onClick={user ? () => navigate('/dashboard') : handleLogin}
              className="w-full bg-[#1565C0] hover:bg-[#12355B] text-white rounded-xl py-6 font-semibold transition-all duration-300 shadow-button"
            >
              {user ? t('nav.backToDashboard') : t('pricing.getStarted')}
            </Button>
          </div>

          {/* Pro Plan */}
          <div className="bg-white dark:bg-[#131824] border-2 border-[#45C04A]/50 rounded-2xl p-8 shadow-card transition-all duration-300 hover:shadow-card-hover relative overflow-hidden flex flex-col" data-testid="pro-plan-card">
            <div className="absolute top-0 right-0 bg-[#45C04A] text-[#12355B] text-xs font-bold px-4 py-1.5 rounded-bl-xl">
              {t('pricing.recommended')}
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-[#45C04A]" />
                <h3 className="font-primary font-bold text-xl text-[#12355B] dark:text-white">{t('pricing.proPlan')}</h3>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('pricing.proPlanDescription')}</p>
            </div>
            <div className="mb-6">
              {isYearly ? (
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-primary font-extrabold text-5xl text-[#12355B] dark:text-white tracking-tight" data-testid="pro-price">$31.20</span>
                    <span className="text-slate-400">{t('pricing.proPlanCurrency')}/mo</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-slate-400 line-through text-sm">$468.00</span>
                    <span className="text-[#45C04A] font-semibold text-sm" data-testid="pro-yearly-total">$374.40/{t('pricing.yearLabel')}</span>
                  </div>
                </div>
              ) : (
                <div>
                  <span className="font-primary font-extrabold text-5xl text-[#12355B] dark:text-white tracking-tight" data-testid="pro-price">$39</span>
                  <span className="text-slate-400 ml-2">{t('pricing.proPlanCurrency')}/mo</span>
                </div>
              )}
            </div>
            <div className="space-y-3 mb-8 flex-1">
              {proFeatures.map((key) => (
                <div key={key} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#45C04A] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">{t(key)}</span>
                </div>
              ))}
            </div>
            <Button
              data-testid="pro-plan-button"
              onClick={() => handlePlanClick('pro')}
              disabled={loadingCheckout !== null || isSameIntervalCurrent('pro')}
              className="w-full bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl py-6 font-bold text-base transition-all duration-300 hover:-translate-y-[1px]"
            >
              {isSameIntervalCurrent('pro')
                ? t('pricing.currentPlan')
                : loadingCheckout === 'pro'
                ? t('pricing.processing')
                : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  {userPlan === 'premium' ? t('pricing.switchToPro') : isCurrentPlan('pro') ? t('pricing.switchInterval') : t('pricing.upgradeNow')}
                </>
              )}
            </Button>
          </div>

          {/* Premium Plan */}
          <div className="bg-[#12355B] dark:bg-[#0D1520] border-2 border-[#FFD700] rounded-2xl p-8 shadow-card-hover relative overflow-hidden flex flex-col" data-testid="premium-plan-card">
            <div className="absolute top-0 right-0 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#12355B] text-xs font-bold px-4 py-1.5 rounded-bl-xl">
              {isYearly ? t('pricing.bestValue') : t('pricing.mostPopular')}
            </div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Gem className="w-5 h-5 text-[#FFD700]" />
                <h3 className="font-primary font-bold text-xl text-white">{t('pricing.premiumPlan')}</h3>
              </div>
              <p className="text-sm text-slate-300">{t('pricing.premiumPlanDescription')}</p>
            </div>
            <div className="mb-6">
              {isYearly ? (
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-primary font-extrabold text-5xl text-white tracking-tight" data-testid="premium-price">$47.20</span>
                    <span className="text-slate-300">{t('pricing.proPlanCurrency')}/mo</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-slate-400 line-through text-sm">$708.00</span>
                    <span className="text-[#FFD700] font-semibold text-sm" data-testid="premium-yearly-total">$566.40/{t('pricing.yearLabel')}</span>
                  </div>
                </div>
              ) : (
                <div>
                  <span className="font-primary font-extrabold text-5xl text-white tracking-tight" data-testid="premium-price">$59</span>
                  <span className="text-slate-300 ml-2">{t('pricing.proPlanCurrency')}/mo</span>
                </div>
              )}
            </div>
            <div className="space-y-3 mb-8 flex-1">
              {premiumFeatures.map((key) => (
                <div key={key} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-[#FFD700] mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-slate-200">{t(key)}</span>
                </div>
              ))}
            </div>
            <Button
              data-testid="premium-plan-button"
              onClick={() => handlePlanClick('premium')}
              disabled={loadingCheckout !== null || isSameIntervalCurrent('premium')}
              className="w-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] hover:from-[#FFE44D] hover:to-[#FFB833] text-[#12355B] rounded-xl py-6 font-bold text-base transition-all duration-300 hover:-translate-y-[1px]"
            >
              {isSameIntervalCurrent('premium')
                ? t('pricing.currentPlan')
                : loadingCheckout === 'premium'
                ? t('pricing.processing')
                : (
                <>
                  <Gem className="w-5 h-5 mr-2" />
                  {isCurrentPlan('premium') ? t('pricing.switchInterval') : t('pricing.upgradePremium')}
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="relative">
          <p data-testid="tax-disclaimer" className="text-center text-xs text-slate-500 dark:text-slate-400 mt-6 mb-2">
            {t('pricing.taxDisclaimer')}
          </p>
          <SubscriptionTerms />
        </div>
      </div>

      <Footer />
    </div>
  );
}
